import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/common/Button';
import { X, AlertCircle, Loader } from 'lucide-react';

/**
 * MiroTalk SFU Conference Room Embed
 * 
 * Displays the video conference in an iframe with:
 * - Role-based access control
 * - Video/Audio controls
 * - Chat, screen sharing, and recordings
 */
const MiroTalkConferenceRoom = () => {
  const { meetingId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useAuth();

  const [mirotalkUrl, setMirotalkUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showExit, setShowExit] = useState(false);
  const [userRole, setUserRole] = useState('USER');
  const [isMiroTalkReady, setIsMiroTalkReady] = useState(false);
  const meetingTitle = location.state?.meetingTitle || 'Video Conference';

  useEffect(() => {
    let isMounted = true;

    const initializeMiroTalk = async () => {
      setLoading(true);
      setError(null);
      setIsMiroTalkReady(false);

      let resolvedUrl = null;

      if (location.state?.mirotalkUrl) {
        resolvedUrl = location.state.mirotalkUrl;
        setUserRole(location.state.role || 'USER');
      } else {
        const stored = sessionStorage.getItem(`mirotalk_url_${meetingId}`);
        if (stored) {
          resolvedUrl = stored;
        }
      }

      if (!resolvedUrl) {
        if (isMounted) {
          setError('Invalid access. Please join via the meeting link.');
          setLoading(false);
        }
        return;
      }

      try {
        await verifyMiroTalkUrl(resolvedUrl);

        if (!isMounted) {
          return;
        }

        setMirotalkUrl(resolvedUrl);
        setIsMiroTalkReady(true);
      } catch (verificationError) {
        if (!isMounted) {
          return;
        }

        setError(verificationError.message);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initializeMiroTalk();

    return () => {
      isMounted = false;
    };
  }, [meetingId, location.state]);

  const verifyMiroTalkUrl = async (url) => {
    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'omit',
      });

      if (response.status === 401) {
        throw new Error('Your meeting access token is invalid or expired. Please request a new link.');
      }

      if (!response.ok) {
        throw new Error(`Unable to open the meeting room. MiroTalk responded with status ${response.status}.`);
      }
    } catch (requestError) {
      if (requestError.message) {
        throw requestError;
      }

      throw new Error('Unable to reach the MiroTalk server. Check that it is running at http://localhost:3010 and try again.');
    }
  };

  const handleExitMeeting = () => {
    setShowExit(true);
  };

  const confirmExit = () => {
    // Log exit event
    if (window.opener) {
      window.close();
    } else {
      navigate('/user/meetings');
    }
  };

  const styles = {
    container: {
      width: '100%',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: theme.colors.background,
    },
    header: {
      backgroundColor: theme.colors.cardBg,
      borderBottom: `1px solid ${theme.colors.border}`,
      padding: '12px 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      zIndex: 100,
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      flex: 1,
    },
    title: {
      fontSize: '16px',
      fontWeight: '600',
      color: theme.colors.text,
      margin: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    status: {
      fontSize: '12px',
      color: theme.colors.textSecondary,
      margin: 0,
    },
    headerRight: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    exitButton: {
      padding: '8px 16px',
      backgroundColor: theme.colors.danger,
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontSize: '13px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    iframeContainer: {
      flex: 1,
      display: 'flex',
      overflow: 'hidden',
    },
    iframe: {
      width: '100%',
      height: '100%',
      border: 'none',
      borderRadius: '0px',
    },
    loadingContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      flexDirection: 'column',
      gap: '16px',
    },
    errorContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      flexDirection: 'column',
      gap: '16px',
      padding: '40px',
      textAlign: 'center',
    },
    errorText: {
      color: theme.colors.danger,
      fontSize: '14px',
      margin: 0,
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modalContent: {
      backgroundColor: theme.colors.cardBg,
      borderRadius: '12px',
      padding: '32px',
      maxWidth: '400px',
      width: '100%',
      boxShadow: theme.shadows.lg,
    },
    modalTitle: {
      fontSize: '18px',
      fontWeight: '700',
      color: theme.colors.text,
      margin: '0 0 12px 0',
    },
    modalText: {
      fontSize: '14px',
      color: theme.colors.textSecondary,
      margin: '0 0 24px 0',
    },
    modalButtons: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
    },
    cancelButton: {
      padding: '10px 20px',
      backgroundColor: theme.colors.background,
      color: theme.colors.text,
      border: `1px solid ${theme.colors.border}`,
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    confirmButton: {
      padding: '10px 20px',
      backgroundColor: theme.colors.danger,
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
  };

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <p style={styles.title}>Meeting Error</p>
          <button
            style={styles.exitButton}
            onClick={() => navigate('/user/meetings')}
          >
            <X size={16} />
            Back to Meetings
          </button>
        </div>
        <div style={styles.iframeContainer}>
          <div style={styles.errorContainer}>
            <AlertCircle size={48} color={theme.colors.danger} />
            <p style={styles.errorText}>{error}</p>
            <p style={{ fontSize: '12px', color: theme.colors.textSecondary, margin: 0 }}>
              Please join via your meeting invitation link.
            </p>
            <Button
              onClick={() => navigate('/user/meetings')}
              style={{ marginTop: '16px' }}
            >
              Request New Link
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <p style={styles.title}>Loading Meeting...</p>
        </div>
        <div style={styles.iframeContainer}>
          <div style={styles.loadingContainer}>
            <Loader size={40} style={{ animation: 'spin 1s linear infinite' }} color={theme.colors.primary} />
            <p style={{ color: theme.colors.textSecondary, margin: 0 }}>
              Initializing video conference...
            </p>
          </div>
          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  if (!mirotalkUrl || !isMiroTalkReady) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <p style={styles.title}>Access Denied</p>
          <button
            style={styles.exitButton}
            onClick={() => navigate('/user/meetings')}
          >
            <X size={16} />
            Back to Meetings
          </button>
        </div>
        <div style={styles.iframeContainer}>
          <div style={styles.errorContainer}>
            <AlertCircle size={48} color={theme.colors.danger} />
            <p style={styles.errorText}>You don't have permission to access this meeting.</p>
            <p style={{ fontSize: '12px', color: theme.colors.textSecondary, margin: 0 }}>
              Please ensure you're using a valid meeting link.
            </p>
            <Button
              onClick={() => navigate('/user/meetings')}
              style={{ marginTop: '16px' }}
            >
              Back to Meetings
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div>
            <p style={styles.title}>
              🎥 {meetingTitle}
            </p>
            <p style={styles.status}>
              {user?.name} • {new Date().toLocaleTimeString()}
            </p>
          </div>
        </div>
        <div style={styles.headerRight}>
          <button
            style={styles.exitButton}
            onClick={handleExitMeeting}
          >
            <X size={16} />
            Exit Meeting
          </button>
        </div>
      </div>

      <div style={styles.iframeContainer}>
        <iframe
          style={styles.iframe}
          src={mirotalkUrl}
          allow={
            userRole === 'ADMIN' || userRole === 'SPEAKER'
              ? "camera; microphone; display-capture; clipboard-read; clipboard-write; web-share; autoplay; picture-in-picture"
              : "clipboard-read; clipboard-write; web-share; autoplay; picture-in-picture"
          }
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
          title="MiroTalk SFU Conference"
        />
      </div>

      {showExit && (
        <div style={styles.modal} onClick={() => setShowExit(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Exit Meeting?</h3>
            <p style={styles.modalText}>
              Are you sure you want to leave this meeting? You can rejoin using the meeting link.
            </p>
            <div style={styles.modalButtons}>
              <button
                style={styles.cancelButton}
                onClick={() => setShowExit(false)}
              >
                Cancel
              </button>
              <button
                style={styles.confirmButton}
                onClick={confirmExit}
              >
                Exit Meeting
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default MiroTalkConferenceRoom;
